/**
 * CommunityGuidelines — Sprint D (Feb 2026). 17 sections, verbatim from legal@.
 * Last Updated: July 17, 2026.
 *
 * Unique to Guidelines:
 *  - Mission callout card at top (red-tinted bg)
 *  - DoHeading / DontHeading (green / red kickers)
 *  - Bold emphasis lines (#0F0F10 or #B91C1C w/ weight 800)
 *  - Cross-links row at bottom → /terms + /privacy
 */
import { Users, FileText, ShieldCheck, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  LegalPageShell, HeroCard, LegalLanguageBar, Section, P, Bullet,
  DoHeading, DontHeading, LegalFooter,
} from '@/components/legal/LegalPrimitives';

function EmphRed({ children }) {
  return (
    <p className="text-[14.5px] mb-2.5" style={{ color: '#B91C1C', fontWeight: 700, lineHeight: '22px' }}>
      {children}
    </p>
  );
}

function EmphBlack({ children }) {
  return (
    <p className="text-[14.5px] mb-2.5" style={{ color: '#0F0F10', fontWeight: 800, lineHeight: '22px' }}>
      {children}
    </p>
  );
}

export default function CommunityGuidelines() {
  return (
    <LegalPageShell title="Community Guidelines">
      <HeroCard icon={Users} title="Meet to Keep Community Guidelines" lastUpdated="July 17, 2026" />
      <LegalLanguageBar docKey="guidelines" />

      {/* Mission callout */}
      <div
        className="rounded-2xl p-4 mb-2"
        style={{ background: '#FEF2F2', border: '1px solid #FEE2E2' }}
        data-testid="guidelines-mission-card"
      >
        <p className="text-[10.5px] font-[900] uppercase tracking-[0.08em] mb-1.5" style={{ color: '#B91C1C' }}>
          Our Mission
        </p>
        <p className="text-[15px] font-[700]" style={{ color: '#0F0F10', lineHeight: '22px' }}>
          Help people build genuine, meaningful relationships in a respectful and safe environment.
        </p>
      </div>

      <P>At Meet to Keep, our mission is simple:</P>
      <P>Every member contributes to the community. These guidelines explain the standards we expect from everyone using Meet to Keep.</P>
      <EmphRed>Failure to follow these guidelines may result in content removal, account restrictions, suspension, or permanent removal from the platform.</EmphRed>

      <Section number={1} title="Be Authentic">
        <P>Meet to Keep is designed for real people seeking genuine connections.</P>
        <DoHeading>You should:</DoHeading>
        <Bullet>Use your real photos.</Bullet>
        <Bullet>Provide accurate profile information.</Bullet>
        <Bullet>Represent yourself honestly.</Bullet>
        <Bullet>Keep your profile up to date.</Bullet>
        <DontHeading>Do not:</DontHeading>
        <Bullet>Create fake profiles.</Bullet>
        <Bullet>Impersonate another person.</Bullet>
        <Bullet>Use someone else&rsquo;s photos without permission.</Bullet>
        <Bullet>Misrepresent your age, identity, or relationship status.</Bullet>
        <Bullet>Create multiple accounts to avoid restrictions.</Bullet>
      </Section>

      <Section number={2} title="Treat Others with Respect">
        <P>Respect is expected in every interaction.</P>
        <Bullet>Be kind.</Bullet>
        <Bullet>Be honest.</Bullet>
        <Bullet>Communicate respectfully.</Bullet>
        <P>Everyone deserves to feel safe using Meet to Keep.</P>
      </Section>

      <Section number={3} title="Zero Tolerance for Harassment">
        <P>The following behavior is prohibited:</P>
        <Bullet>Threats</Bullet>
        <Bullet>Intimidation</Bullet>
        <Bullet>Bullying</Bullet>
        <Bullet>Stalking</Bullet>
        <Bullet>Hate speech</Bullet>
        <Bullet>Repeated unwanted contact</Bullet>
        <Bullet>Sexual harassment</Bullet>
        <Bullet>Sharing private information without permission</Bullet>
        <Bullet>Encouraging self-harm</Bullet>
        <Bullet>Violent language</Bullet>
        <Bullet>Revenge content</Bullet>
        <EmphBlack>If someone says no, respect their decision.</EmphBlack>
      </Section>

      <Section number={4} title="No Scams or Fraud">
        <P>Meet to Keep has zero tolerance for scams.</P>
        <DontHeading>You may not:</DontHeading>
        <Bullet>Ask users for money.</Bullet>
        <Bullet>Request gift cards.</Bullet>
        <Bullet>Request cryptocurrency.</Bullet>
        <Bullet>Promote investment opportunities.</Bullet>
        <Bullet>Run romance scams.</Bullet>
        <Bullet>Pretend to be military personnel.</Bullet>
        <Bullet>Pretend to be celebrities.</Bullet>
        <Bullet>Pretend to work for Meet to Keep.</Bullet>
        <Bullet>Attempt identity theft.</Bullet>
        <P>Any attempt to financially exploit another user may result in immediate permanent removal.</P>
      </Section>

      <Section number={5} title="Keep Conversations Appropriate">
        <P>Respect personal boundaries.</P>
        <DontHeading>Do not send:</DontHeading>
        <Bullet>Unwanted sexual messages.</Bullet>
        <Bullet>Explicit photos.</Bullet>
        <Bullet>Graphic content.</Bullet>
        <Bullet>Repeated unwanted advances.</Bullet>
        <Bullet>Abusive language.</Bullet>
        <EmphBlack>Consent matters both online and offline.</EmphBlack>
      </Section>

      <Section number={6} title="Profile Photos">
        <DoHeading>Profile photos should:</DoHeading>
        <Bullet>Clearly show you.</Bullet>
        <Bullet>Be appropriate for a general audience.</Bullet>
        <Bullet>Avoid excessive editing that materially misrepresents your appearance.</Bullet>
        <DontHeading>Photos may not include:</DontHeading>
        <Bullet>Nudity.</Bullet>
        <Bullet>Pornographic content.</Bullet>
        <Bullet>Graphic violence.</Bullet>
        <Bullet>Illegal activity.</Bullet>
        <Bullet>Weapons displayed in a threatening manner.</Bullet>
        <Bullet>Hate symbols.</Bullet>
        <Bullet>Copyrighted images you do not have permission to use.</Bullet>
        <P>We may remove photos that violate these guidelines.</P>
      </Section>

      <Section number={7} title="Illegal Activity">
        <P>Meet to Keep may not be used to facilitate illegal conduct.</P>
        <P>Examples include:</P>
        <Bullet>Human trafficking.</Bullet>
        <Bullet>Sexual exploitation.</Bullet>
        <Bullet>Child exploitation.</Bullet>
        <Bullet>Drug trafficking.</Bullet>
        <Bullet>Fraud.</Bullet>
        <Bullet>Money laundering.</Bullet>
        <Bullet>Extortion.</Bullet>
        <Bullet>Blackmail.</Bullet>
        <Bullet>Theft.</Bullet>
        <Bullet>Violent crime.</Bullet>
        <P>We may cooperate with law enforcement when legally required.</P>
      </Section>

      <Section number={8} title="Protect Your Privacy">
        <P>For your safety, avoid sharing:</P>
        <Bullet>Social Security numbers.</Bullet>
        <Bullet>Bank account information.</Bullet>
        <Bullet>Credit card numbers.</Bullet>
        <Bullet>Passwords.</Bullet>
        <Bullet>Government ID numbers.</Bullet>
        <Bullet>Home address too early in a conversation.</Bullet>
        <Bullet>Workplace security details.</Bullet>
        <Bullet>Sensitive financial information.</Bullet>
        <EmphBlack>Never send money to someone you have only met online.</EmphBlack>
      </Section>

      <Section number={9} title="Meet Safely">
        <P>When meeting someone:</P>
        <Bullet>Meet in a public place.</Bullet>
        <Bullet>Tell a trusted friend where you are going.</Bullet>
        <Bullet>Arrange your own transportation.</Bullet>
        <Bullet>Keep your phone charged.</Bullet>
        <Bullet>Trust your instincts.</Bullet>
        <Bullet>Leave immediately if you feel uncomfortable.</Bullet>
        <EmphBlack>Your safety comes first.</EmphBlack>
      </Section>

      <Section number={10} title="AI Features">
        <P>Meet to Keep uses AI to improve the experience through features such as conversation starters and safety tools.</P>
        <P>Please remember:</P>
        <Bullet>AI suggestions are optional.</Bullet>
        <Bullet>AI can make mistakes.</Bullet>
        <Bullet>Use your own judgment.</Bullet>
        <Bullet>You remain responsible for everything you send.</Bullet>
      </Section>

      <Section number={11} title="Reporting Problems">
        <P>If you experience:</P>
        <Bullet>Harassment</Bullet>
        <Bullet>Scams</Bullet>
        <Bullet>Fake profiles</Bullet>
        <Bullet>Threats</Bullet>
        <Bullet>Inappropriate content</Bullet>
        <Bullet>Spam</Bullet>
        <Bullet>Impersonation</Bullet>
        <Bullet>Safety concerns</Bullet>
        <P>Please use the Report feature.</P>
        <P>Reports help protect the entire community.</P>
        <P>False reports made maliciously may also violate these guidelines.</P>
      </Section>

      <Section number={12} title="Blocking Users">
        <P>You may block any user at any time.</P>
        <P>Blocking prevents further communication between you and that user.</P>
        <P>Users should not attempt to contact someone who has blocked them through alternate accounts.</P>
      </Section>

      <Section number={13} title="Advertising and Promotion">
        <P>Meet to Keep is not a marketplace.</P>
        <DontHeading>Without our permission, do not:</DontHeading>
        <Bullet>Advertise businesses.</Bullet>
        <Bullet>Promote OnlyFans or similar services.</Bullet>
        <Bullet>Recruit users.</Bullet>
        <Bullet>Sell products.</Bullet>
        <Bullet>Solicit donations.</Bullet>
        <Bullet>Conduct surveys.</Bullet>
        <Bullet>Post referral links.</Bullet>
        <Bullet>Spam promotional messages.</Bullet>
      </Section>

      <Section number={14} title="Spam and Automation">
        <DontHeading>You may not:</DontHeading>
        <Bullet>Use bots.</Bullet>
        <Bullet>Automatically send messages.</Bullet>
        <Bullet>Mass-like profiles using automation.</Bullet>
        <Bullet>Scrape user data.</Bullet>
        <Bullet>Use scripts or unauthorized software.</Bullet>
        <Bullet>Attempt to manipulate the matching system.</Bullet>
      </Section>

      <Section number={15} title="Respect Intellectual Property">
        <P>Only upload content you own or have permission to use.</P>
        <DontHeading>Do not upload:</DontHeading>
        <Bullet>Copyrighted photos.</Bullet>
        <Bullet>Movies.</Bullet>
        <Bullet>TV clips.</Bullet>
        <Bullet>Music.</Bullet>
        <Bullet>Logos.</Bullet>
        <Bullet>Trademarks belonging to others without permission.</Bullet>
      </Section>

      <Section number={16} title="Account Enforcement">
        <P>Depending on the severity of a violation, Meet to Keep may:</P>
        <Bullet>Remove content.</Bullet>
        <Bullet>Issue a warning.</Bullet>
        <Bullet>Restrict certain features.</Bullet>
        <Bullet>Suspend an account.</Bullet>
        <Bullet>Permanently ban an account.</Bullet>
        <Bullet>Report illegal conduct to the appropriate authorities where required or permitted by law.</Bullet>
        <P>Serious violations may result in immediate permanent removal without prior warning.</P>
      </Section>

      <Section number={17} title="Our Commitment">
        <P>We are committed to building a platform where people can form meaningful relationships in a respectful environment.</P>
        <P>While we work to reduce harmful behavior through moderation, reporting tools, and automated detection, no platform can guarantee that every interaction will be safe or positive. Users should always exercise good judgment when interacting with others online or meeting in person.</P>
        <EmphBlack>By using Meet to Keep, you agree to follow these Community Guidelines and help foster a welcoming community for everyone.</EmphBlack>
      </Section>

      {/* Cross-links row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6" data-testid="guidelines-cross-links">
        <Link
          to="/terms"
          data-testid="guidelines-open-terms"
          className="flex items-center gap-3 rounded-2xl p-4 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
          style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.06)' }}
        >
          <FileText size={20} style={{ color: '#B91C1C' }} strokeWidth={2}/>
          <span className="flex-1 text-[14px] font-[700] text-[#0F0F10]">Terms of Service</span>
          <ChevronRight size={16} style={{ color: '#9CA3AF' }}/>
        </Link>
        <Link
          to="/privacy"
          data-testid="guidelines-open-privacy"
          className="flex items-center gap-3 rounded-2xl p-4 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-shadow"
          style={{ background: '#FFFFFF', border: '1px solid rgba(15,15,16,0.06)' }}
        >
          <ShieldCheck size={20} style={{ color: '#B91C1C' }} strokeWidth={2}/>
          <span className="flex-1 text-[14px] font-[700] text-[#0F0F10]">Privacy Policy</span>
          <ChevronRight size={16} style={{ color: '#9CA3AF' }}/>
        </Link>
      </div>

      <LegalFooter />
    </LegalPageShell>
  );
}
